import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from "react";

const defaultContextValue = {
  readOnly: false,
  hasUnsavedWork: false,
  registerFlush: () => () => {},
  flushAll: async () => {},
  registerUnsavedWork: () => () => {},
  flushUnsavedWork: async () => {},
  hasUnsavedWorkNow: () => false,
  hasPageBoundWorkNow: () => false,
  heldFieldCountNow: () => 0
};

export const ListingEditContext = createContext(defaultContextValue);

export const useListingEditReadOnly = () => useContext(ListingEditContext).readOnly;

export const useListingFlushRegistry = () => {
  const { registerFlush, flushAll } = useContext(ListingEditContext);
  return { registerFlush, flushAll };
};

export const useListingUnsavedWorkRegistry = () => {
  const {
    readOnly,
    registerUnsavedWork,
    flushUnsavedWork,
    hasUnsavedWork,
    hasUnsavedWorkNow,
    hasPageBoundWorkNow,
    heldFieldCountNow
  } = useContext(ListingEditContext);
  return { readOnly, registerUnsavedWork, flushUnsavedWork, hasUnsavedWork, hasUnsavedWorkNow, hasPageBoundWorkNow, heldFieldCountNow };
};

/**
 * Listing editor provider: read-only flag, unsaved-work registry, and publish flush registry.
 *
 * `flushAllRef` lets the listing page (parent of this provider) await the same `flushAll`
 * that sections register through context.
 *
 * @param {Object} props
 * @param {boolean} [props.readOnly=false]
 * @param {string} [props.editorKey]
 * @param {{ current: Function|null }} [props.flushAllRef]
 * @param {React.ReactNode} props.children
 */
export const ListingEditProvider = ({ readOnly = false, editorKey = "", flushAllRef, children }) => {
  const workRef = useRef(new Map());
  const generationRef = useRef(0);
  const flushFnsRef = useRef(new Set());
  const [hasUnsavedWork, setHasUnsavedWork] = useState(false);

  const registerFlush = useCallback(fn => {
    flushFnsRef.current.add(fn);
    return () => {
      flushFnsRef.current.delete(fn);
    };
  }, []);

  const flushAll = useCallback(async () => {
    await Promise.allSettled([...flushFnsRef.current].map(fn => Promise.resolve().then(fn)));
  }, []);

  useLayoutEffect(() => {
    const parentFlushAllRef = flushAllRef;
    if (!parentFlushAllRef) {
      return undefined;
    }
    parentFlushAllRef.current = flushAll;
    return () => {
      if (parentFlushAllRef.current === flushAll) {
        parentFlushAllRef.current = null;
      }
    };
  }, [flushAll, flushAllRef]);

  useLayoutEffect(() => {
    generationRef.current += 1;
    workRef.current = new Map();
    setHasUnsavedWork(false);
  }, [editorKey]);

  const recount = useCallback(() => {
    let dirty = false;
    workRef.current.forEach(entry => {
      if (entry.getIsDirty()) {
        dirty = true;
      }
    });
    setHasUnsavedWork(dirty);
  }, []);

  const registerUnsavedWork = useCallback(
    (key, entry) => {
      const generation = generationRef.current;
      workRef.current.set(key, entry);
      recount();
      return () => {
        if (generation !== generationRef.current) {
          return;
        }

        const existing = workRef.current.get(key);

        // Defer tombstoning so a same-key re-register (isDirty toggle) replaces this
        // entry before we freeze it. A real unmount still has `existing` in the map.
        queueMicrotask(() => {
          if (generation !== generationRef.current) {
            return;
          }

          if (workRef.current.get(key) !== existing) {
            return;
          }

          // Page-bound edits cannot be saved and are gone with their page (the member was asked before
          // leaving it), so they leave no tombstone behind (GROWTH-8017).
          if (!existing?.getIsDirty() || existing.isPageBound) {
            workRef.current.delete(key);
            recount();
            return;
          }

          let stillDirty = true;
          const originalFlush = existing.flush;
          const tombstone = {
            getIsDirty: () => stillDirty,
            flush: async () => {
              const persisted = await Promise.resolve()
                .then(() => originalFlush?.())
                .then(result => Boolean(result))
                .catch(() => false);

              if (!persisted) {
                return false;
              }

              stillDirty = false;
              if (workRef.current.get(key) === tombstone) {
                workRef.current.delete(key);
              }
              recount();
              return true;
            }
          };

          workRef.current.set(key, tombstone);
          recount();
          tombstone.flush();
        });
      };
    },
    [recount]
  );

  const flushUnsavedWork = useCallback(async () => {
    const flushes = [...workRef.current.values()].map(entry => Promise.resolve().then(() => entry.flush?.()));
    await Promise.allSettled(flushes);
    recount();
  }, [recount]);

  const hasUnsavedWorkNow = useCallback(() => {
    let dirty = false;
    workRef.current.forEach(entry => {
      if (entry.getIsDirty()) {
        dirty = true;
      }
    });
    return dirty;
  }, []);

  // A section holds edits that a switch to another page of the editor would lose (GROWTH-8017).
  const hasPageBoundWorkNow = useCallback(() => {
    let bound = false;
    workRef.current.forEach(entry => {
      if (entry.isPageBound && entry.getIsDirty()) {
        bound = true;
      }
    });
    return bound;
  }, []);

  // How many fields hold a value that cannot be saved until it is fixed (GROWTH-8025): Publish refuses while there are any.
  const heldFieldCountNow = useCallback(() => {
    let count = 0;
    workRef.current.forEach(entry => {
      if (entry.getIsDirty()) {
        count += entry.getHeldFields?.() || 0;
      }
    });
    return count;
  }, []);

  const value = useMemo(
    () => ({
      readOnly,
      hasUnsavedWork,
      registerUnsavedWork,
      flushUnsavedWork,
      hasUnsavedWorkNow,
      hasPageBoundWorkNow,
      heldFieldCountNow,
      registerFlush,
      flushAll
    }),
    [
      readOnly,
      hasUnsavedWork,
      registerUnsavedWork,
      flushUnsavedWork,
      hasUnsavedWorkNow,
      hasPageBoundWorkNow,
      heldFieldCountNow,
      registerFlush,
      flushAll
    ]
  );

  return <ListingEditContext.Provider value={value}>{children}</ListingEditContext.Provider>;
};
